/**
 * Declines by first-click source.
 *
 * The channel is the person's FIRST touch, from the attribution engine's lead
 * rule — the price book is not consulted, because a refused checkout has not
 * been sold to anyone yet. Each person keeps one channel across every attempt.
 *
 * Two views side by side: attempts (the same stacked chart as the other
 * breakdowns, so processor/customer read identically) and people (acceptance
 * and unrecovered amount), because for a channel question the person is the
 * unit — one Reddit prospect retrying four times is one Reddit prospect.
 *
 * AMOUNTS ARRIVE IN CENTS; formatCurrency takes dollars. See DeclinesSection.
 */

import { Card, CardHeader, CardTitle, CardContent } from './ui'
import DeclineBreakdownChart from './DeclineBreakdownChart'
import { channelColor } from '../lib/channels'

const pct = value => (typeof value === 'number' ? `${(value * 100).toFixed(1)}%` : '—')

function coverageNote(coverage, formatNumber) {
  if (!coverage?.requested) return null
  const parts = [
    coverage.fromPatients && `${formatNumber(coverage.fromPatients)} from patient records`,
    coverage.fromLeads && `${formatNumber(coverage.fromLeads)} from lead records`,
    coverage.fromBigQuery && `${formatNumber(coverage.fromBigQuery)} from site analytics`,
  ].filter(Boolean)
  const placed = coverage.requested - coverage.unplaced
  return `${formatNumber(placed)} of ${formatNumber(coverage.requested)} people placed${parts.length ? ` (${parts.join(', ')})` : ''}; the rest are Other.` +
    (coverage.bigQuerySkipped ? ` Site analytics was not checked: ${coverage.bigQuerySkipped}.` : '')
}

export default function DeclineSourceSection({ rows, coverage, formatCurrency, formatNumber }) {
  if (!rows) {
    return (
      <p className="text-sm text-muted-foreground">Source attribution could not be loaded for this range; the rest of the report is unaffected.</p>
    )
  }
  const people = [...rows].sort((a, b) => b.uniqueAttempted - a.uniqueAttempted || b.attempted - a.attempted)
  return (
    <section className="space-y-4" aria-label="Declines by first-click source">
      <div>
        <h2 className="text-lg font-semibold">Declines by first-click source</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Each person is credited to the channel of their first recorded visit, not to whoever later closed the sale.
          {' '}{coverageNote(coverage, formatNumber)}
        </p>
      </div>
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <DeclineBreakdownChart title="Declined attempts by source" rows={rows} />
        <Card>
          <CardHeader>
            <CardTitle className="text-base">People by source</CardTitle>
            <p className="text-xs text-muted-foreground">People grouped by email. Unique declines never paid in this range; unrecovered is the largest amount each of them was refused.</p>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <table className="w-full text-sm tabular-nums">
              <thead>
                <tr className="text-xs text-muted-foreground">
                  {['Source', 'People', 'Paid', 'Unique declines', 'Acceptance', 'Recovered', 'Unrecovered'].map((heading, i) => (
                    <th key={heading} scope="col" className={`py-2 font-medium ${i === 0 ? 'text-left' : 'text-right'}`}>{heading}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {people.map(row => (
                  <tr key={row.channel} className="border-t">
                    <th scope="row" className="py-2 text-left font-normal">
                      <span className="flex items-center gap-2">
                        <span className="h-2.5 w-2.5 flex-shrink-0 rounded-full" style={{ backgroundColor: row.channel === 'no_email' ? '#d1d5db' : channelColor(row.channel) }} />
                        {row.name}
                      </span>
                    </th>
                    <td className="py-2 text-right">{formatNumber(row.uniqueAttempted)}</td>
                    <td className="py-2 text-right">{formatNumber(row.uniquePaid)}</td>
                    <td className="py-2 text-right">{formatNumber(row.uniqueDeclines)}</td>
                    <td className="py-2 text-right">{pct(row.acceptanceRate)}</td>
                    <td className="py-2 text-right">{pct(row.recoveryRate)}</td>
                    <td className="py-2 text-right">{formatCurrency((row.lostAmount || 0) / 100)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>
    </section>
  )
}
